/* ==========================================================================
   dewy — product-form.js

   Two jobs, both of which genuinely need script:

     1. Variant switching without a page load (single-variant products skip it).
     2. Arming the auto-refill add-on, per size.
     3. Tagging waitlist sign-ups with the size that was selected.

   Everything else in the buy box is CSS on the checkbox state — the panel
   lifting, the frequency options appearing, the shipping note swapping. None
   of that is here, because none of it needs to be.

   THE SAFETY RULE, in one line: an un-ticked refills box must never post a
   selling_plan. The markup ships with no frequency selected, so the failure
   mode of this file not loading is a plain one-off preorder — exactly what an
   un-ticked box promises. The opposite arrangement (pre-select a plan, rely on
   script to clear it) would enrol people in a subscription they never chose
   the moment anything went wrong. Do not invert this.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Variant switching
     --------------------------------------------------------------------- */
  function initVariants(root) {
    var dataEl = root.querySelector('[data-variant-data]');
    if (!dataEl) return; // single-variant product — nothing to switch

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return; // malformed payload: leave the server-rendered variant in place
    }

    // Option controls are either <select>s or groups of radios (the size
    // cards). Both carry data-variant-option="<index>".
    var controls = Array.prototype.slice.call(root.querySelectorAll('[data-variant-option]'));
    var optionCount = 0;
    controls.forEach(function (c) {
      optionCount = Math.max(optionCount, parseInt(c.getAttribute('data-variant-option'), 10) + 1);
    });
    var idInputs = Array.prototype.slice.call(root.querySelectorAll('[data-variant-id]'));
    var buttons  = Array.prototype.slice.call(root.querySelectorAll('button[name="add"]'));
    var waitlistTags = root.querySelector('[data-waitlist-tags]');
    if (!controls.length || (!idInputs.length && !waitlistTags)) return;

    var nowEls = root.querySelectorAll('[data-price-now]');
    var ctaEls = root.querySelectorAll('[data-cta-price]');
    var wasEls = root.querySelectorAll('.price .po');

    function paint(els, value) {
      Array.prototype.forEach.call(els, function (el) { if (value) el.textContent = value; });
    }

    function chosenValue(i) {
      var hit = null;
      controls.forEach(function (c) {
        if (parseInt(c.getAttribute('data-variant-option'), 10) !== i) return;
        if (c.type === 'radio') { if (c.checked) hit = c.value; }
        else hit = c.value;
      });
      return hit;
    }

    function findVariant() {
      var chosen = [];
      for (var i = 0; i < optionCount; i++) chosen.push(chosenValue(i));
      return variants.filter(function (v) {
        return chosen.every(function (opt, i) { return v.options[i] === opt; });
      })[0];
    }

    function update() {
      var variant = findVariant();

      // No such combination — disable buying rather than posting a bad id.
      if (!variant) {
        buttons.forEach(function (b) { b.disabled = true; });
        return;
      }

      idInputs.forEach(function (input) { input.value = variant.id; });

      // Waitlist: record which size they were looking at.
      if (waitlistTags) {
        waitlistTags.value = waitlistTags.getAttribute('data-base') + ',' + variant.handle_tag;
      }

      buttons.forEach(function (b) { b.disabled = !variant.available; });

      /* Repaint the price to match the variant that will actually be charged.
         Without this, a multi-variant product with per-variant pricing shows
         the first variant's price in the headline and on the button while the
         cart charges a different one — the worst kind of quiet bug, because
         the shopper only finds out at checkout. Single-variant products never
         reach this function at all, so it costs them nothing. */
      paint(nowEls, variant.price_formatted);
      paint(ctaEls, variant.price_formatted);

      Array.prototype.forEach.call(wasEls, function (el) {
        var value = variant.compare_at_price_formatted;
        if (value) {
          el.textContent = value;
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });

      // After the base price is painted, tell the refills control which size
      // is live so it can repaint with the plan price if refills are on.
      root.dispatchEvent(new CustomEvent('dewy:variant', { detail: { id: String(variant.id), prices: variant } }));

      // Keep the URL shareable and the back button honest.
      if (window.history && window.history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        } catch (e) { /* older browser, not worth failing over */ }
      }
    }

    controls.forEach(function (c) { c.addEventListener('change', update); });
    update();
  }

  /* ------------------------------------------------------------------------
     Refills add-on

     Ticking the box selects a frequency; un-ticking clears it. Clearing is
     what actually keeps the cart honest — a stale checked radio inside the
     form would still post.

     The button price follows the real charge. If the selling plan is a plain
     price lock with no discount (the intended setup) every price is identical
     and nothing visibly moves; if the plan does discount, the button says so
     rather than quoting a figure the cart won't honour.
     --------------------------------------------------------------------- */
  function initRefills(root) {
    var box = root.querySelector('[data-refill-box]');
    if (!box) return; // no selling plans on this product

    var toggle = box.querySelector('[data-refill-toggle]');
    var groups = Array.prototype.slice.call(box.querySelectorAll('[data-plan-variant]'));
    if (!toggle || !groups.length) return;

    var idInput = root.querySelector('[data-variant-id]');
    var ctaEl = root.querySelector('[data-cta-price]');
    var nowEl = root.querySelector('[data-price-now]');
    var basePrice = ctaEl ? ctaEl.textContent : null;

    function activeGroup() {
      var id = idInput ? String(idInput.value) : null;
      return groups.filter(function (g) { return g.getAttribute('data-plan-variant') === id; })[0] || null;
    }

    function radios(g) { return g ? Array.prototype.slice.call(g.querySelectorAll('input[type="radio"]')) : []; }

    function paint() {
      var g = activeGroup();
      var value = basePrice;
      if (toggle.checked && g) {
        var hit = g.querySelector('input[type="radio"]:checked');
        if (hit && hit.getAttribute('data-plan-price')) value = hit.getAttribute('data-plan-price');
      }
      if (!value) return;
      if (ctaEl) ctaEl.textContent = value;
      if (nowEl) nowEl.textContent = value;
    }

    function sync() {
      var g = activeGroup();

      // This size has no refill plan: hide the box and make sure nothing posts.
      box.hidden = !g;
      if (!g) toggle.checked = false;

      groups.forEach(function (group) {
        var live = group === g;
        group.hidden = !live;
        radios(group).forEach(function (r) {
          if (!live || !toggle.checked) { r.checked = false; r.disabled = true; }
          else { r.disabled = false; }
        });
      });

      if (g && toggle.checked && !g.querySelector('input[type="radio"]:checked')) {
        var first = radios(g)[0];
        if (first) first.checked = true;
      }
      paint();
    }

    toggle.addEventListener('change', sync);
    groups.forEach(function (g) {
      radios(g).forEach(function (r) { r.addEventListener('change', paint); });
    });

    // A size change repaints the base price first (initVariants), then this
    // re-reads it so the un-ticked state shows the new size's price.
    root.addEventListener('dewy:variant', function (e) {
      if (e.detail && e.detail.prices) basePrice = e.detail.prices.price_formatted;
      sync();
    });

    sync();
  }

  /* ------------------------------------------------------------------------
     One owner for the selling plan

     Subscription apps commonly inject their own purchase-options widget into
     whatever product form they find. When that lands inside this form, two
     controls both submit `selling_plan` and the order gets whichever one the
     browser serialises last — not necessarily the one the shopper looked at.

     So: while this theme's refills control is present, it owns the decision.
     Anything else named selling_plan inside the same form is disabled at
     submit, which excludes it from the POST without deleting the app's markup
     or interfering with the app elsewhere.

     If you would rather the app's widget own it, switch off "Use this theme's
     refills control" in the section settings. Then this guard never runs,
     because there is nothing here to conflict with.
     --------------------------------------------------------------------- */
  function guardSellingPlan(root) {
    var picker = root.querySelector('[data-refill-box]');
    if (!picker) return; // this theme isn't offering refills — app owns it

    var form = picker.closest('form');
    if (!form) return;

    form.addEventListener('submit', function () {
      var mine = picker.querySelectorAll('[name="selling_plan"]');
      var all = form.querySelectorAll('[name="selling_plan"]');
      var foreign = 0;

      Array.prototype.forEach.call(all, function (input) {
        var isMine = Array.prototype.indexOf.call(mine, input) !== -1;
        if (!isMine) {
          input.disabled = true; // disabled controls are not submitted
          foreign++;
        }
      });

      if (foreign && window.console && console.warn) {
        console.warn(
          '[dewy] Ignored ' + foreign + ' selling_plan input(s) from another ' +
          'app inside the product form. Pick one owner: either switch off ' +
          '"Use this theme\'s refills control", or stop the app rendering its ' +
          'widget on this page.'
        );
      }
    });
  }

  function initAll() {
    document.querySelectorAll('.buy').forEach(function (root) {
      initRefills(root);
      initVariants(root);
      guardSellingPlan(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-bind after the theme editor re-renders the section.
  document.addEventListener('shopify:section:load', initAll);
})();
